import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  MenuItem,
  Select,
} from '@mui/material';
import PropTypes from 'prop-types';
import {
  memo,
  useEffect,
  useRef,
  useState
} from 'react';
import Loader from 'src/common/Loader';
import { APIService } from 'src/api/api';
import ReactECharts from 'src/common/ReactECharts';
import { DATE_FILTER_LIST, chartInputStyles, getVehicleUsageChartConfig } from './chartConfig';

const VehicleGroupConsumptionChart = ({ sx, ...props }) => {
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(DATE_FILTER_LIST[0].id);
  const [usageData, setUsageData] = useState(null);
  const isMountedRef = useRef(false);
  const requestIdRef = useRef(0);

  const formatToEChartData = (data) => {
    const chartDataArray = Object.keys(data) || [];
    const dataSeries = chartDataArray.map((item) => {
      const itemName = item === 'external_quantity' ? 'Vendor Vehicles' : 'Company Vehicles';
      const value = data[item] || 0;
      return {
        name: itemName,
        value: value.toFixed(0),
      };
    });
    const config = { radius: ['80%'] };
    const chartData = getVehicleUsageChartConfig(dataSeries, config);
    setUsageData(chartData);
  };

  const fetchChartData = async (date) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (isMountedRef.current) {
      setLoading(true);
    }

    try {
      const res = await APIService.getTopFuelConsumption(date);
      if (!isMountedRef.current || requestId !== requestIdRef.current) {
        return;
      }

      if (!res.success) throw res;
      formatToEChartData(res.result);
    } catch (error) {
      if (!isMountedRef.current || requestId !== requestIdRef.current) {
        return;
      }

      console.log('dashboard sales error', error.message);
    } finally {
      if (isMountedRef.current && requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    fetchChartData(selectedDate);
  }, []);

  const handleDateChange = (e) => {
    const selectedTimeline = e.target.value;
    setSelectedDate(selectedTimeline);
    fetchChartData(selectedTimeline);
  };

  return (
    <Card
      {...props}
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...sx
      }}
    >
      <CardHeader
        title="Group-wise Fuel Consumption"
        sx={{
          backgroundColor: '#fff',
          color: '#111827'
        }}
        action={(
          <Select
            name="date"
            variant="standard"
            onChange={handleDateChange}
            value={selectedDate}
            sx={chartInputStyles}
          >
            {DATE_FILTER_LIST.map((item) => (
              <MenuItem value={item.id} key={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </Select>
        )}
      />
      <Divider />
      <CardContent sx={{ px: 0, flex: 1 }}>
        <Box sx={{ height: 400, position: 'relative' }}>
          {loading || !usageData ? (
            <Loader />
          ) : (
            <ReactECharts options={usageData} />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default memo(VehicleGroupConsumptionChart);

VehicleGroupConsumptionChart.propTypes = {
  sx: PropTypes.object
};
